import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import './StationDetails.scss'
import AddPumpModal from './AddPumpModal'
import EditPumpModal from './EditPumpModal'
import EditTankModal from './EditTankModal'
import StationLocationModal from './StationLocationModal'

import breadcrumbBack from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/breadcrumb-back.svg'
import breadcrumbChevron from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/breadcrumb-chevron.svg'
import userManager from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/user-manager.svg'
import pumpIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/pump-icon.svg'
import tankIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/tank-icon.svg'
import filterIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/filter-list.svg'
import exportIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Stations/export-list.svg'

import fillingStationsService, {
  FillingStation,
  FillingStationPump,
} from '../../../../../../BoostXpressAdmin/Services/fillingStationsService'
import { HorizontalLoader, ModalWrapper } from 'Components'
import { handleError, handleSuccess } from 'Helpers'
import posService from 'Services/posService'
import { PosAccountType } from 'Models/posAccountType'
import { ToastContainer } from 'react-toastify'
import Swal from 'sweetalert2'
import AssignPosTerminalAccount from 'Pages/Frontend/Components/PosTerminals/AssignPosTerminalAccount'

type TabType = 'pumps' | 'terminals' | 'tanks' | 'history' | 'delivery'

const StationDetails = () => {
  const navigate = useNavigate()
  const { id } = useParams()

  const [isLoading, setIsLoading] = useState(false)
  const [station, setStation] = useState<FillingStation | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>('pumps')
  const [isAddPumpModalOpen, setIsAddPumpModalOpen] = useState(false)
  const [isEditPumpModalOpen, setIsEditPumpModalOpen] = useState(false)
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false)
  const [selectedPump, setSelectedPump] = useState<FillingStationPump | null>(null)
  const [selectedTankIndex, setSelectedTankIndex] = useState<number | null>(null)
  const [pumps, setPumps] = useState<FillingStationPump[]>([])

  const [terminals, setTerminals] = useState<PosAccountType[]>([])

  const [showPosModal, setShowPosModal] = useState(false)
  const togglePosModal = () => {
    setShowPosModal(!showPosModal)
    getTerminals()
  }

  const getStationDetails = async () => {
    if (!id) return

    setIsLoading(true)
    await fillingStationsService.getById(id).then(
      (res) => {
        setStation(res.data ?? null)
        setPumps(res.data?.fuelPumps ?? [])
        setIsLoading(false)
      },
      (error) => {
        setStation(null)
        setPumps([])
        setIsLoading(false)
      },
    )
  }

  useEffect(() => {
    getStationDetails()
  }, [id])

  const fuelTypes = useMemo(() => {
    const types = (station?.products ?? []).map((p) => p.product).filter(Boolean) as string[]
    return Array.from(new Set(types))
  }, [station])

  const handleEditPump = (pump: FillingStationPump) => {
    setSelectedPump(pump)
    setIsEditPumpModalOpen(true)
  }

  const handleDeletePump = async (pump: FillingStationPump) => {
    if (!window.confirm(`Are you sure you want to delete pump "${pump.pumpName}"?`)) {
      return
    }

    try {
      await fillingStationsService.deletePump(id!, pump.id)
      handleSuccess('Pump deleted successfully')
      getStationDetails()
    } catch (error: any) {
      handleError(error)
    }
  }

  const handleDeleteTerminal = async (terminal: PosAccountType) => {
    Swal.fire({
      title: 'Are you sure you want to delete terminal?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await posService.deleteAccountPosTerminal(terminal.id).then(
          () => {
            handleSuccess('Terminal deleted successfully')
            getStationDetails()
          },
          (error: any) => {
            handleError(error)
          },
        )
      }
    })
  }

  const handleDeleteStationImage = async (image: any) => {
    if (!window.confirm('Are you sure you want to delete this station image?')) {
      return
    }

    try {
      await fillingStationsService.deleteStationImage(id!, image.keyFromCloudProvider)
      handleSuccess('Station image deleted successfully')
      getStationDetails()
    } catch (error: any) {
      handleError(error)
    }
  }
  const getTerminals = async () => {
    await posService.terminalList(1, 'ASC', station?.accountManagerAccountId).then(
      (response) => {
        console.log(response)
        setTerminals(response.data?.data.dataset)
      },
      (error) => {
        handleError(error)
      },
    )
  }

  useEffect(() => {
    if (station) {
      getTerminals()
    }
  }, [station])

  const renderTabContent = () => {
    switch (activeTab) {
      case 'pumps':
        return (
          <div className='bx-station-details-page__tab-content'>
            <div className='bx-station-details-page__tab-header'>
              <span className='bx-tab-title'>{pumps.length} Pumps Registered</span>
              <button className='bx-tab-add-btn' onClick={() => setIsAddPumpModalOpen(true)}>
                + Add Pump
              </button>
            </div>
            <div className='bx-station-details-page__card-grid'>
              {pumps.length === 0 ? (
                <div className='bx-empty-state'>No pumps registered</div>
              ) : (
                pumps.map((pump) => (
                  <div key={pump.id} className='bx-item-card'>
                    <div className='bx-item-card__body'>
                      <div className='bx-item-card__icon-wrap'>
                        <img src={pumpIcon} alt='' />
                      </div>
                      <div className='bx-item-card__info'>
                        <span className='bx-item-card__name'>{pump.pumpName}</span>
                        <span className='bx-item-card__type'>{pump.productType}</span>
                      </div>
                      <span
                        className={`bx-item-card__status ${!pump.isActive ? 'bx-item-card__status--inactive' : ''}`}
                      >
                        {pump.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className='bx-item-card__nozzles'>
                      <span className='bx-item-card__nozzle-label'>Nozzles</span>
                      <div className='bx-item-card__nozzle-list'>
                        {pump.nozzles.length ? (
                          pump.nozzles.map((nozzle) => (
                            <span key={nozzle} className='bx-item-card__nozzle-badge'>
                              {nozzle}
                            </span>
                          ))
                        ) : (
                          <span className='bx-item-card__nozzle-empty'>Not configured</span>
                        )}
                      </div>
                    </div>
                    <div className='bx-item-card__footer'>
                      <button
                        className='bx-item-card__btn bx-item-card__btn--edit'
                        onClick={() => handleEditPump(pump)}
                      >
                        Edit
                      </button>
                      <button
                        className='bx-item-card__btn bx-item-card__btn--remove'
                        onClick={() => handleDeletePump(pump)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )
      case 'terminals':
        return (
          <div className='bx-station-details-page__tab-content'>
            <div className='bx-station-details-page__tab-header'>
              {terminals.length > 0 ? (
                <span className='bx-tab-title'>{terminals.length} Terminals Registered</span>
              ) : (
                <span className='bx-tab-title'>No Terminals Registered</span>
              )}
              <button className='bx-tab-add-btn' onClick={() => togglePosModal()}>
                + Add Terminal
              </button>
            </div>
            <div className='bx-station-details-page__card-grid'>
              {terminals.length > 0 &&
                terminals.map((terminal) => (
                  <div key={terminal.id} className='bx-item-card'>
                    <div className='bx-item-card__body'>
                      <div className='bx-item-card__icon-wrap'>
                        <img src={pumpIcon} alt='' />
                      </div>
                      <div className='bx-item-card__info'>
                        <span className='bx-item-card__name'>{terminal.posTerminalId}</span>
                        <span className='bx-item-card__type'>{terminal.provider}</span>
                        <span className='bx-item-card__type'>
                          Authorization Code: {terminal.authorizationCode}
                        </span>
                      </div>
                      <span className='bx-item-card__status'>
                        {terminal.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className='bx-item-card__footer'>
                      <Link
                        to={`/boostxpress/bx-pos-terminal-transactions/${terminal.posTerminalId}`}
                        className='text-decoration-none'
                      >
                        <button className='bx-item-card__btn bx-item-card__btn--edit px-4 btn-default'>
                          Transactions
                        </button>
                      </Link>
                      <button
                        className='bx-item-card__btn bx-item-card__btn--remove'
                        onClick={() => handleDeleteTerminal(terminal)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )
      case 'tanks':
        return (
          <div className='bx-station-details-page__tab-content'>
            <div className='bx-station-details-page__tab-header'>
              <span className='bx-tab-title'>
                {station?.tanks?.length ? station.tanks.length : 'No'} Tanks Installed
              </span>
              {/* <button className='bx-tab-add-btn'>+ Add Tank</button> */}
            </div>
            <div className='bx-station-details-page__card-grid'>
              {station?.tanks?.map((tank, index) => (
                <div key={index} className='bx-item-card'>
                  <div className='bx-item-card__body'>
                    <div className='bx-item-card__icon-wrap'>
                      <img src={tankIcon} alt='' />
                    </div>
                    <div className='bx-item-card__info'>
                      <span className='bx-item-card__name'>
                        {tank.name || `TANK 0${index + 1}`}
                      </span>
                      <span className='bx-item-card__type'>{tank.product}</span>
                    </div>
                    <span
                      className={`bx-item-card__status ${tank.quantity === 0 ? 'bx-item-card__status--inactive' : ''}`}
                    >
                      {tank.quantity === 0 ? 'Inactive' : 'Active'}
                    </span>
                  </div>
                  <div className='bx-item-card__footer'>
                    <button
                      type='button'
                      className='bx-item-card__btn bx-item-card__btn--edit'
                      onClick={() => setSelectedTankIndex(index)}
                    >
                      Edit
                    </button>
                    <button className='bx-item-card__btn bx-item-card__btn--remove'>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      case 'history':
        return (
          <div className='bx-station-details-page__tab-content'>
            <div className='bx-station-details-page__tab-header'>
              <span className='bx-tab-title'>Order History</span>
            </div>
            <div className='bx-station-details-page__table-card'>
              <div className='bx-station-details-page__toolbar'>
                <div className='bx-search-input-wrap'>
                  <svg
                    className='bx-search-icon'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                  >
                    <circle cx='11' cy='11' r='8' />
                    <path d='M21 21l-4.35-4.35' />
                  </svg>
                  <input type='text' placeholder='Search Transaction ID or User' />
                </div>
                <button className='bx-toolbar-btn'>
                  <img src={filterIcon} alt='' />
                  Filters
                </button>
                <button className='bx-toolbar-btn'>
                  <img src={exportIcon} alt='' />
                  Export
                </button>
              </div>
              <table className='bx-station-details-page__table'>
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>User</th>
                    <th>Fuel Type</th>
                    <th>Litres</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                    <tr key={i}>
                      <td>TXN-88291</td>
                      <td>Chidi Okonkwo</td>
                      <td>PMS</td>
                      <td>13.7L</td>
                      <td>₦8,500</td>
                      <td>POS</td>
                      <td>{i === 1 ? 'Today 09:14' : '16-02-2026 09:14'}</td>
                      <td>
                        <span
                          className={`bx-status-pill bx-status-pill--${i === 1 ? 'pending' : 'success'}`}
                        >
                          {i === 1 ? 'Pending' : 'Success'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      case 'delivery':
        return (
          <div className='bx-station-details-page__tab-content'>
            <div className='bx-station-details-page__tab-header'>
              <span className='bx-tab-title'>Product Delivery</span>
            </div>
            <div className='bx-station-details-page__table-card'>
              <div className='bx-station-details-page__toolbar'>
                <div className='bx-search-input-wrap'>
                  <svg
                    className='bx-search-icon'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                  >
                    <circle cx='11' cy='11' r='8' />
                    <path d='M21 21l-4.35-4.35' />
                  </svg>
                  <input type='text' placeholder='Search Order ID' />
                </div>
                <button className='bx-toolbar-btn'>
                  <img src={filterIcon} alt='' />
                  Filters
                </button>
                <button className='bx-toolbar-btn'>
                  <img src={exportIcon} alt='' />
                  Export
                </button>
              </div>
              <table className='bx-station-details-page__table'>
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Product Type</th>
                    <th>Ordered Quantity</th>
                    <th>Qauntity Delivered</th>
                    <th>Confirmation Timestamp</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                    <tr key={i}>
                      <td>TXN-88291</td>
                      <td>PMS</td>
                      <td>5000L</td>
                      <td>0L</td>
                      <td>16-02-2026 09:14</td>
                      <td>
                        <span className='bx-status-pill bx-status-pill--confirmed'>Confirmed</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      default:
        return null
    }
  }

  if (isLoading)
    return (
      <div className='bx-station-details-page d-flex justify-content-center align-items-center vh-100'>
        <HorizontalLoader />
      </div>
    )
  if (!station) return <div className='bx-station-details-page'>Station not found.</div>

  const pumpProductTypes = Array.from(
    new Set((station.products ?? []).map((product) => product.product)),
  )

  return (
    <div className='bx-station-details-page'>
      <div className='bx-station-details-page__breadcrumb'>
        <button className='bx-station-details-page__back' onClick={() => navigate(-1)}>
          <img src={breadcrumbBack} alt='Back' />
        </button>
        <div className='bx-station-details-page__crumbs'>
          <Link to='/boostxpress/stations' className='bx-station-details-page__crumb-link'>
            Stations Management
          </Link>
          <img src={breadcrumbChevron} alt='' className='bx-station-details-page__crumb-sep' />
          <span className='bx-station-details-page__crumb-current'>Station Details</span>
        </div>
      </div>

      <div className='bx-station-details-page__header'>
        <div className='bx-station-details-page__info'>
          <div className='bx-station-details-page__title-row'>
            <h2 className='bx-station-details-page__title'>{station.businessName}</h2>
            <span className='bx-station-details-page__status'>Active</span>
          </div>
          <div className='bx-station-details-page__meta'>
            <div className='bx-meta-item'>
              {station.city}, {station.state}
            </div>
            <div className='bx-meta-dot' />
            <div className='bx-meta-item'>{fuelTypes.join(', ')}</div>
            <div className='bx-meta-dot' />
            <div className='bx-meta-item bx-meta-item--manager'>
              <img src={userManager} alt='' />
              {station.accountManager
                ? `${station.accountManager.firstName} ${station.accountManager.lastName}`
                : 'David Daye'}
            </div>
          </div>
        </div>
        <div className='bx-station-details-page__actions'>
          <button
            className='bx-station-details-page__btn bx-station-details-page__btn--edit'
            onClick={() => navigate(`/boostxpress/stations/${id}/edit`)}
          >
            Edit Station
          </button>
          <button
            className='bx-station-details-page__btn bx-station-details-page__btn--edit border border-info text-info'
            onClick={() => setIsLocationModalOpen(true)}
          >
            Station Location
          </button>
          <button className='bx-station-details-page__btn bx-station-details-page__btn--deactivate'>
            Deactivate
          </button>
        </div>
      </div>

      <div className='row'>
        <div className='col-12 col-md-4'>
          <div id='stationImagesCarousel' className='carousel slide border' data-bs-ride='carousel'>
            <div className='carousel-indicators'>
              {station?.stationImages && station.stationImages.length > 0 ? (
                station.stationImages.map((_: any, index: number) => (
                  <button
                    key={index}
                    type='button'
                    data-bs-target='#stationImagesCarousel'
                    data-bs-slide-to={index}
                    className={index === 0 ? 'active' : ''}
                    aria-current={index === 0 ? 'true' : 'false'}
                    aria-label={`Slide ${index + 1}`}
                  />
                ))
              ) : (
                <button
                  type='button'
                  data-bs-target='#stationImagesCarousel'
                  data-bs-slide-to={0}
                  className='active'
                  aria-current='true'
                  aria-label='Slide 1'
                />
              )}
            </div>
            <div className='carousel-inner'>
              {station?.stationImages && station.stationImages.length > 0 ? (
                station.stationImages.map((image: any, index: number) => (
                  <div
                    key={index}
                    className={`carousel-item ${index === 0 ? 'active' : ''} position-relative`}
                  >
                    <img
                      src={image.url}
                      className='d-block w-100 h-auto'
                      alt={`Station image ${index + 1}`}
                      style={{ height: '300px', objectFit: 'cover' }}
                    />
                    <button
                      className='btn btn-danger btn-sm position-absolute top-0 end-0 m-2'
                      onClick={() => handleDeleteStationImage(image)}
                      style={{ zIndex: 10 }}
                    >
                      <i className='bi bi-trash'></i> Delete
                    </button>
                  </div>
                ))
              ) : (
                <div className='carousel-item active'>
                  <img
                    src='https://via.placeholder.com/400x300/cccccc/666666?text=No+Station+Image'
                    className='d-block w-100'
                    alt='No station image available'
                    style={{ height: '300px', objectFit: 'cover' }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='col-12 col-lg-8'>
          <div className='bx-station-details-page__metrics row'>
            {/* <div className='bx-metric-card bx-metric-card--blue'>
          <span className='bx-metric-card__label'>Today&lsquo;s Volume</span>
          <span className='bx-metric-card__value'>₦2.4M</span>
          <span className='bx-metric-card__meta'>All fuel types</span>
        </div> */}
            {station?.products?.map((product, index) => (
              <div
                key={index}
                className={`col-md-4 m-md-1 bx-metric-card bx-metric-card--${index === 0 ? 'green' : index === 1 ? 'teal' : 'blue'}`}
              >
                <span className='bx-metric-card__label'>{product.product} Price/L</span>
                <span className='bx-metric-card__value'>₦{product.price}</span>
                <span className='bx-metric-card__meta'>Per litre</span>
              </div>
            ))}
            {/* <div className='col-md-4 m-md-1 bx-metric-card bx-metric-card--orange'>
          <span className='bx-metric-card__label'>Pump Uptime</span>
          <span className='bx-metric-card__value'>83%</span>
          <span className='bx-metric-card__meta'>5 of 6 active</span>
        </div>   */}
          </div>
        </div>
      </div>

      <section className='bx-details-section w-100'>
        <h4 className='bx-details-section-title'>Station Information</h4>
        <div className='bx-personal-info-box'>
          <div className='bx-personal-info-content'>
            <div className='bx-personal-info-row row bx-personal-info-row--top'>
              <div className='col-md-3 mt-3'>
                <div className='bx-info-group'>
                  <span className='bx-info-label-text'>Station ID:</span>
                  <span className='bx-info-value-text'>{station?.id || 'N/A'}</span>
                </div>
              </div>

              <div className='col-md-3 mt-3'>
                <div className='bx-info-group'>
                  <span className='bx-info-label-text'>Business Name</span>
                  <span className='bx-info-value-text'>{station?.businessName || 'N/A'}</span>
                </div>
              </div>

              <div className='col-md-3 mt-3'>
                <div className='bx-info-group'>
                  <span className='bx-info-label-text'>Branch Name</span>
                  <span className='bx-info-value-text'>{station?.branchName || 'N/A'}</span>
                </div>
              </div>

              <div className='col-md-3 mt-3'>
                <div className='bx-info-group'>
                  <span className='bx-info-label-text'>Address</span>
                  <span className='bx-info-value-text'>{station?.address || 'N/A'}</span>
                </div>
              </div>

              <div className='col-md-3 mt-3'>
                <div className='bx-info-group'>
                  <span className='bx-info-label-text'>City</span>
                  <span className='bx-info-value-text'>{station?.city || 'N/A'}</span>
                </div>
              </div>

              <div className='col-md-3 mt-3'>
                <div className='bx-info-group'>
                  <span className='bx-info-label-text'>State:</span>
                  <span className='bx-info-value-text'>{station?.state || 'N/A'}</span>
                </div>
              </div>

              <div className='col-md-3 mt-3'>
                <div className='bx-info-group'>
                  <span className='bx-info-label-text'>Account Manager ID:</span>
                  <span className='bx-info-value-text'>
                    {station?.accountManagerAccountId || 'N/A'}
                  </span>
                </div>
              </div>

              <div className='col-md-3 mt-3'>
                <div className='bx-info-group'>
                  <span className='bx-info-label-text'>Fuel Fact Company ID:</span>
                  <span className='bx-info-value-text'>{station?.fuelFactCompanyId || 'N/A'}</span>
                </div>
              </div>

              <div className='col-md-3 mt-3'>
                <div className='bx-info-group'>
                  <span className='bx-info-label-text'>Fuel Fact Company Branch ID:</span>
                  <span className='bx-info-value-text'>
                    {station?.fuelFactCompanyBranchId || 'N/A'}
                  </span>
                </div>
              </div>

              <div className='col-md-3 mt-3'>
                <div className='bx-info-group'>
                  <span className='bx-info-label-text'>Opening Time:</span>
                  <span className='bx-info-value-text'>{station?.openingTimeHour || 'N/A'}</span>
                </div>
              </div>

              <div className='col-md-3 mt-3'>
                <div className='bx-info-group'>
                  <span className='bx-info-label-text'>Closing Time:</span>
                  <span className='bx-info-value-text'>{station?.closingTimeHour || 'N/A'}</span>
                </div>
              </div>

              <div className='col-md-3 mt-3'>
                <div className='bx-info-group'>
                  <span className='bx-info-label-text'>Created Date:</span>
                  <span className='bx-info-value-text'>
                    {station?.createdAtISO8601
                      ? new Date(station.createdAtISO8601).toLocaleDateString()
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className='bx-station-details-page__tabs-container'>
        <div className='bx-station-details-page__tabs-nav'>
          <button
            className={`bx-station-details-page__tab-btn ${activeTab === 'pumps' ? 'bx-station-details-page__tab-btn--active' : ''}`}
            onClick={() => setActiveTab('pumps')}
          >
            Pumps ({station?.fuelPumps?.length || 0})
          </button>
          <button
            className={`bx-station-details-page__tab-btn ${activeTab === 'terminals' ? 'bx-station-details-page__tab-btn--active' : ''}`}
            onClick={() => setActiveTab('terminals')}
          >
            Terminals ({terminals.length})
          </button>
          <button
            className={`bx-station-details-page__tab-btn ${activeTab === 'tanks' ? 'bx-station-details-page__tab-btn--active' : ''}`}
            onClick={() => setActiveTab('tanks')}
          >
            Tanks ({station?.tanks?.length || 0})
          </button>
          {/* <button
            className={`bx-station-details-page__tab-btn ${activeTab === 'history' ? 'bx-station-details-page__tab-btn--active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Order History
          </button> */}
          {/* <button
            className={`bx-station-details-page__tab-btn ${activeTab === 'delivery' ? 'bx-station-details-page__tab-btn--active' : ''}`}
            onClick={() => setActiveTab('delivery')}
          >
            Product Delivery
          </button> */}
        </div>

        {renderTabContent()}
      </div>

      <AddPumpModal
        isOpen={isAddPumpModalOpen}
        onClose={() => setIsAddPumpModalOpen(false)}
        onSuccess={() => {
          // Refresh pumps data after successful addition
          getStationDetails()
        }}
        stationId={id}
        productTypes={pumpProductTypes}
      />

      <EditPumpModal
        isOpen={isEditPumpModalOpen}
        onClose={() => {
          setIsEditPumpModalOpen(false)
          setSelectedPump(null)
        }}
        onSuccess={() => {
          // Refresh pumps data after successful edit
          getStationDetails()
          setSelectedPump(null)
        }}
        stationId={id}
        pump={selectedPump}
        productTypes={pumpProductTypes}
      />

      {station && selectedTankIndex !== null && station.tanks[selectedTankIndex] && (
        <EditTankModal
          station={station}
          tankIndex={selectedTankIndex}
          onClose={() => setSelectedTankIndex(null)}
          onSuccess={() => { void getStationDetails() }}
        />
      )}

      <StationLocationModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        station={station}
      />

      <ModalWrapper
        showModal={showPosModal}
        closeModal={togglePosModal}
        title='Assign Account to POS Terminal'
        hideHeader={false}
      >
        <AssignPosTerminalAccount
          toggleModal={togglePosModal}
          accountId={station?.accountManager?.msisdn}
        />
      </ModalWrapper>
      <ToastContainer />
    </div>
  )
}

export default StationDetails
